/**
 * The block palette — what can be added, and what a new one is.
 *
 * SEVENTEEN TYPES, not the twenty-four the mockup draws. DECISION-021 cut seven
 * long-tail types from the MVP set — image select, image, video, ranking,
 * matrix, slider, file upload — and `packages/schema`'s `BLOCK_TYPES` is the
 * list that survived. RULE-014 settles which one wins: "`packages/schema` is
 * the only funnel validator… Do not let it emerge from the UI." A palette
 * offering a type the validator rejects is a button that produces a 422.
 *
 * The seven groups are the story's, and they are also the schema's own — the
 * comments in `BLOCK_TYPES` divide the same list the same way, which is why
 * `packages/app/test/editor-blocks.test.ts` can assert the grouping covers the
 * enum exactly rather than trusting this file to stay in step with it.
 *
 * Every factory here produces a block `funnelSchema` ACCEPTS. That is asserted,
 * because the alternative is a palette that adds an invalid block and an
 * autosave that 422s a second later with no idea which click caused it.
 *
 * Story 6.3.
 */
import {
  BLOCK_LABEL,
  BLOCK_TYPES,
  blockWithDefaults,
  newFieldKey,
  type Block,
  type BlockArgs,
  type BlockType,
} from '@leadgible/schema'

export type PaletteGroup = {
  label: string
  types: readonly BlockType[]
}

/** The seven groups, in the export's own order. */
export const PALETTE: readonly PaletteGroup[] = [
  { label: 'Choice', types: ['card_select', 'dropdown'] },
  { label: 'Text', types: ['short_text', 'long_text'] },
  { label: 'Contact', types: ['email', 'phone', 'name'] },
  { label: 'Number', types: ['number', 'currency', 'scale'] },
  { label: 'Date', types: ['date'] },
  { label: 'Advanced', types: ['hidden_field', 'consent'] },
  { label: 'Content', types: ['intro', 'divider', 'rich_text', 'outcome'] },
]

/**
 * The operator's word for each type — the palette's labels, and what a new
 * question asks until it is written. The vocabulary's own since Story 27.2
 * (`add_question` writes it), re-exported where the editor has always read it.
 */
export { BLOCK_LABEL }

/** Blocks that ask something. The rest are content and have no inspector fields. */
export const CONTENT_TYPES: readonly BlockType[] = ['intro', 'divider', 'rich_text', 'outcome']
export const isContent = (type: BlockType): boolean => CONTENT_TYPES.includes(type)

/** Blocks whose answer is chosen from a list — the ones with an Options panel. */
export const OPTION_TYPES: readonly BlockType[] = ['card_select', 'dropdown']
export const hasOptions = (type: BlockType): boolean => OPTION_TYPES.includes(type)

/**
 * A short, stable, collision-resistant suffix.
 *
 * Ids here are LOCAL to one funnel document and never leave it as an entity
 * identifier, so `lib/ids.ts`'s prefixed opaque ids would be the wrong tool —
 * those are for rows. What this needs is only to not collide inside one array.
 */
let counter = 0
const suffix = (): string => {
  counter += 1
  return `${counter.toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

export const newBlockId = (): string => `blk_${suffix()}`
export const newStepId = (): string => `step_${suffix()}`
export const newOptionId = (): string => `opt_${suffix()}`
/** A routing action's id. Local to one funnel's `routing[]` — Story 9.1. */
export const newActionId = (): string => `act_${suffix()}`

/**
 * A field key that is legal on the first try: the type, then the type and a
 * counter, so two Email blocks do not collide. `fieldKey` is `lower_snake_case`
 * starting with a letter, and it is the ONE identifier in a funnel that reaches
 * other people's systems — exports and GoHighLevel mappings both key on it,
 * which is what the inspector's warning copy is about. The vocabulary's own
 * since Story 27.2, re-exported.
 */
export { newFieldKey }

/**
 * A new block of a type, valid the moment it exists — what the palette adds.
 *
 * The vocabulary's `blockWithDefaults` since Story 27.2, with a fresh id: the
 * schema's minimums and not invented policy — a question needs words, a choice
 * one option, a currency its code, a scale its ends — and anything the schema
 * leaves optional left off, because an inspector field the operator has not
 * filled in should be empty, not a guess they then have to delete. NO OPTION
 * CARRIES A `value` (Q-044): `value ?? id` must equal `id`, or the option scores
 * `default_points` and nothing says so. One definition of new: the palette's
 * `add_question` writes exactly this.
 */
export function newBlock(type: BlockType, taken: ReadonlySet<string>): Block {
  return blockWithDefaults({ type, id: newBlockId() } as BlockArgs, taken) as Block
}

/** Every type the palette offers, flat — for the tests and for a type guard. */
export const PALETTE_TYPES: readonly BlockType[] = PALETTE.flatMap((group) => group.types)

/** The enum, re-exported so a consumer never has to reach past this module. */
export { BLOCK_TYPES }
